import React, { useEffect, useMemo, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AppShell from "../components/layout/AppShell";
import { Button, Badge, Spinner } from "../components/common/UIKit";
import PresetBar from "../components/common/PresetBar";
import { listLeads, downloadLeadsCsv } from "../api/leads";
import { LEAD_CACHE_KEY, getLeadRouteId } from "../utils/leadDetail";
import { levelToMin } from "../components/common/ConfidenceScorePicker";
import {
  ChevronLeft, ChevronRight, Phone, Columns3, PanelLeftClose,
  Download, RefreshCw, ArrowLeft,
} from "lucide-react";

const RESULTS_CACHE_KEY = "leads_results_cache_v1";
const RESULTS_CACHE_TTL_MIN = parseInt(process.env.REACT_APP_LEAD_EXPIRE_IN_MINUTES || "5", 10);

function readResultsCache() {
  try {
    const raw = sessionStorage.getItem(RESULTS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.timestamp) return null;
    const ageMs = Date.now() - parsed.timestamp;
    if (ageMs > RESULTS_CACHE_TTL_MIN * 60 * 1000) {
      sessionStorage.removeItem(RESULTS_CACHE_KEY);
      return null;
    }
    return parsed;
  } catch (e) { return null; }
}

function writeResultsCache(payload) {
  try {
    sessionStorage.setItem(RESULTS_CACHE_KEY, JSON.stringify({ ...payload, timestamp: Date.now() }));
  } catch (e) { /* ignore */ }
}

function clearResultsCache() {
  try { sessionStorage.removeItem(RESULTS_CACHE_KEY); } catch (e) { /* ignore */ }
}

const STATUS_LABELS = {
  fsbo: { label: "FSBO", color: "red" },
  canceled: { label: "CANCELED", color: "zinc" },
  expired: { label: "EXPIRED", color: "zinc" },
  withdrawn: { label: "WITHDRAWN", color: "zinc" },
  nod: { label: "NOD", color: "green" },
  probate: { label: "PROBATE", color: "purple" },
  tax_liens: { label: "TAX LIENS", color: "amber" },
  divorce: { label: "DIVORCE", color: "zinc" },
};

const ALL_COLUMNS = [
  { key: "status", label: "Status" },
  { key: "date", label: "Date" },
  { key: "owner", label: "Owner" },
  { key: "address", label: "Address" },
  { key: "phone", label: "Phone" },
  { key: "email", label: "Email" },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "zip", label: "ZIP" },
  { key: "county", label: "County" },
  { key: "property_use", label: "Property Use" },
  { key: "beds", label: "Beds" },
  { key: "baths", label: "Baths" },
  { key: "sqft", label: "Sqft" },
  { key: "year_built", label: "Year Built" },
  { key: "price", label: "Price" },
  { key: "amount_due", label: "Amount Due" },
  { key: "institution", label: "Institution" },
];

const DEFAULT_COLUMNS = [
  "status", "date", "owner", "address", "phone", "email",
  "city", "state", "zip", "county", "property_use", "beds", "baths", "price",
];

const COLUMN_STORAGE_KEY = "leads_visible_columns_v1";
const COLUMN_WIDTHS_KEY = "leads_column_widths_v1";

const firstNonEmpty = (...vals) => {
  for (const v of vals) {
    if (v !== null && v !== undefined && String(v).trim() !== "" && String(v).toLowerCase() !== "nan") return v;
  }
  return null;
};
const joinName = (first, last, middle) => {
  const parts = [first, middle, last].filter((p) => p && String(p).toLowerCase() !== "nan");
  return parts.length ? parts.join(" ").trim() : null;
};
function deriveOwner(lead) {
  return firstNonEmpty(
    lead.owner_1_name, lead.property_owned,
    joinName(lead.first_name, lead.last_name, lead.middle_name),
    joinName(lead.defendant_first_name, lead.defendant_last_name, lead.defendant_middle_name),
    joinName(lead.pr_first_name, lead.pr_last_name, lead.pr_middle_name),
    joinName(lead.plaintiff_first_name, lead.plaintiff_last_name, lead.plaintiff_middle_name),
    lead.full_name, lead.list_agent_name, lead.borrower_name_owner,
  );
}
function derivePhone(lead) {
  return firstNonEmpty(lead.owner_phone_1, lead.phone_one, lead.contact_phone, lead.pr_phone_1, lead.list_agent_phone, lead.phone_two);
}
function deriveEmail(lead) {
  return firstNonEmpty(lead.owner_email_1, lead.email_one, lead.pr_email_1, lead.list_agent_email);
}
function deriveStatus(lead) {
  return firstNonEmpty(lead.source_model, lead.document_type);
}
function deriveDate(lead) {
  const v = firstNonEmpty(lead.file_date, lead.date, lead.processed_date, lead.date_created);
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v).slice(0, 10) : d.toISOString().slice(0, 10);
}
function deriveYearBuilt(lead) {
  const v = lead.year_built;
  if (!v) return null;
  const m = String(v).match(/\d{4}/);
  return m ? m[0] : null;
}
function formatPhone(v) {
  if (v === null || v === undefined) return null;
  const raw = String(v).trim();
  if (!raw || raw.toLowerCase() === "nan") return null;
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  return digits.length >= 7 ? raw : null;
}

function ColumnPicker({ visible, setVisible }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const toggle = (key) => {
    setVisible(visible.includes(key) ? visible.filter((k) => k !== key) : [...visible, key]);
  };
  useEffect(() => {
    const handler = (e) => {
      if (open && menuRef.current && !menuRef.current.contains(e.target) && buttonRef.current && !buttonRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);
  return (
    <div className="relative">
      <button ref={buttonRef} type="button" onClick={() => setOpen((o) => !o)}
        className="h-8 inline-flex items-center gap-1.5 px-3 rounded-md border border-zinc-200 bg-white text-xs font-semibold text-zinc-700 hover:border-zinc-950 hover:text-zinc-950">
        <Columns3 className="h-3.5 w-3.5" /> Columns <span className="text-zinc-400">({visible.length})</span>
      </button>
      {open && (
        <div ref={menuRef} className="absolute right-0 top-9 z-30 w-64 max-h-80 overflow-y-auto bg-white border border-zinc-200 rounded-md shadow-lg p-2">
          <div className="flex items-center justify-between px-2 py-1 mb-1 border-b border-zinc-100">
            <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">Show columns</span>
            <button onClick={() => setVisible(DEFAULT_COLUMNS)} className="text-[11px] text-zinc-500 hover:text-zinc-950 font-semibold">Reset</button>
          </div>
          {ALL_COLUMNS.map((c) => (
            <label key={c.key} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-zinc-50 cursor-pointer">
              <input type="checkbox" checked={visible.includes(c.key)} onChange={() => toggle(c.key)}
                className="h-4 w-4 rounded border-zinc-300 text-zinc-950 focus:ring-zinc-950" />
              <span className="text-sm text-zinc-900">{c.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export default function LeadsResultsPage() {
  const location = useLocation();
  const navigate = useNavigate();

  // Decide initial filters/data on mount: if the page was reached via the
  // filter form (fromFilters) we always do a fresh fetch and ignore any cache.
  // Otherwise (e.g. coming back from a lead detail page) we restore the cached
  // snapshot so the agent doesn't re-pay for an identical fetch.
  const initialMountRef = useRef(null);
  if (initialMountRef.current === null) {
    const fromFilters = location.state?.fromFilters === true;
    const stateFilters = location.state?.filters || null;
    if (fromFilters) {
      clearResultsCache();
      initialMountRef.current = { filters: stateFilters || {}, cached: null };
    } else {
      const cached = readResultsCache();
      if (cached?.data) {
        initialMountRef.current = { filters: cached.filters || stateFilters || {}, cached };
      } else {
        initialMountRef.current = { filters: stateFilters || {}, cached: null };
      }
    }
  }
  const initialMount = initialMountRef.current;

  const [filters] = useState(initialMount.filters);
  const [page, setPage] = useState(initialMount.cached?.page || 1);
  const [pageSize, setPageSize] = useState(initialMount.cached?.pageSize || 50);
  const [data, setData] = useState(initialMount.cached?.data || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const [visible, setVisibleState] = useState(() => {
    try {
      const raw = localStorage.getItem(COLUMN_STORAGE_KEY);
      if (raw) { const p = JSON.parse(raw); if (Array.isArray(p) && p.length) return p; }
    } catch (e) { /* ignore */ }
    return DEFAULT_COLUMNS;
  });
  const setVisible = (next) => {
    setVisibleState(next);
    try { localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(next)); } catch (e) { /* ignore */ }
  };

  const [columnWidths, setColumnWidths] = useState(() => {
    try { const raw = localStorage.getItem(COLUMN_WIDTHS_KEY); const p = raw ? JSON.parse(raw) : {}; return p && typeof p === "object" ? p : {}; }
    catch (e) { return {}; }
  });
  const persistColumnWidths = (next) => { try { localStorage.setItem(COLUMN_WIDTHS_KEY, JSON.stringify(next)); } catch (e) { /* ignore */ } };
  const startColumnResize = (key, e) => {
    e.preventDefault(); e.stopPropagation();
    const th = e.currentTarget.parentElement;
    const startX = e.clientX;
    const startWidth = columnWidths[key] || (th ? th.offsetWidth : 120);
    document.body.style.cursor = "col-resize"; document.body.style.userSelect = "none";
    const onMove = (ev) => { const w = Math.max(60, Math.round(startWidth + (ev.clientX - startX))); setColumnWidths((prev) => ({ ...prev, [key]: w })); };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = ""; document.body.style.userSelect = "";
      setColumnWidths((prev) => { persistColumnWidths(prev); return prev; });
    };
    document.addEventListener("mousemove", onMove); document.addEventListener("mouseup", onUp);
  };

  const buildParams = (p = page, ps = pageSize) => {
    const params = { page: p, page_size: ps };
    Object.entries(filters).forEach(([k, v]) => {
      if (k === "confidence_level") return;
      if (Array.isArray(v)) { if (v.length) params[k] = v; }
      else if (v !== "" && v !== null && v !== undefined) params[k] = v;
    });
    if (filters.confidence_level) params.confidence_score_min = levelToMin(filters.confidence_level);
    return params;
  };

  const fetchLeads = async (p = page, ps = pageSize) => {
    // Any new fetch invalidates the snapshot used for detail-page round-trips.
    clearResultsCache();
    setLoading(true); setError(null);
    try {
      const resp = await listLeads(buildParams(p, ps));
      setData(resp);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load leads");
      setData(null);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    // If we hydrated from cache on mount, skip the fetch.
    if (initialMount.cached?.data) return;
    fetchLeads(initialMount.cached?.page || 1, initialMount.cached?.pageSize || pageSize);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPage(newPage); fetchLeads(newPage, pageSize);
  };
  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize); setPage(1); fetchLeads(1, newSize);
  };

  const downloadCsv = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const params = buildParams(1, 1);
      delete params.page; delete params.page_size;
      const resp = await downloadLeadsCsv(params);
      const cd = resp.headers?.["content-disposition"] || "";
      const match = /filename="?([^";]+)"?/i.exec(cd);
      const filename = match?.[1] || `leads-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.csv`;
      const blob = resp.data instanceof Blob ? resp.data : new Blob([resp.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Download failed");
    } finally { setDownloading(false); }
  };

  const results = data?.results || data?.data || (Array.isArray(data) ? data : []);
  const total = data?.count || data?.total || results.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const openLeadDetails = (lead, idx) => {
    const leadId = getLeadRouteId(lead, idx);
    try {
      const raw = sessionStorage.getItem(LEAD_CACHE_KEY);
      const cache = raw ? JSON.parse(raw) : {};
      cache[leadId] = lead;
      sessionStorage.setItem(LEAD_CACHE_KEY, JSON.stringify(cache));
    } catch (e) { /* ignore */ }
    // Snapshot the current results so navigating back from the detail page
    // restores the same view without a refetch (TTL-bounded).
    writeResultsCache({ filters, data, page, pageSize });
    navigate(`/leads/${leadId}`, { state: { lead } });
  };

  const hasMapFilter = !!filters.map_polygon;

  return (
    <AppShell>
      <div className="flex flex-col h-[100vh]">
        <PresetBar mode="save" currentFilters={filters} />
        {/* Top bar */}
        <div className="px-6 py-3 border-b border-zinc-200 flex items-center justify-between gap-4 bg-white flex-shrink-0">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/leads", { state: { filters } })}
              className="h-8 px-3 rounded-md border border-zinc-300 bg-white text-zinc-950 hover:border-zinc-950 hover:bg-zinc-50 inline-flex items-center gap-1.5 text-xs font-semibold"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to Filters
            </button>
            <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">
              <span>{total.toLocaleString()} results</span>
              <span className="mx-2 text-zinc-300">·</span>
              <span>Page {page.toLocaleString()} of {totalPages.toLocaleString()}</span>
            </div>
            {hasMapFilter && (
              <span className="h-6 px-2 rounded-full bg-blue-100 text-blue-700 text-[11px] font-semibold inline-flex items-center">
                Map filter active
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-zinc-600">
            <button type="button" onClick={downloadCsv} disabled={downloading}
              className="h-8 inline-flex items-center gap-1.5 px-3 rounded-md border border-zinc-200 bg-white text-xs font-semibold text-zinc-700 hover:border-zinc-950 hover:text-zinc-950 disabled:opacity-60 disabled:cursor-not-allowed">
              {downloading ? <><span className="h-3.5 w-3.5 rounded-full border-2 border-zinc-300 border-t-zinc-950 animate-spin" />Preparing…</> : <><Download className="h-3.5 w-3.5" /> Download</>}
            </button>
            <button type="button" onClick={() => fetchLeads(page, pageSize)} disabled={loading}
              className="h-8 inline-flex items-center gap-1.5 px-3 rounded-md border border-zinc-200 bg-white text-xs font-semibold text-zinc-700 hover:border-zinc-950 hover:text-zinc-950 disabled:opacity-50 disabled:cursor-not-allowed">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <ColumnPicker visible={visible} setVisible={setVisible} />
            <label htmlFor="results-page-size" className="font-semibold uppercase tracking-widest text-zinc-500">Rows</label>
            <select id="results-page-size" value={pageSize} onChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
              className="h-8 rounded-md border border-zinc-200 bg-white px-2 text-xs font-semibold text-zinc-950 focus:outline-none focus:ring-2 focus:ring-zinc-950">
              {[10, 20, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-auto bg-white">
          {loading && (
            <div className="flex items-center justify-center py-16 text-sm text-zinc-500">
              <Spinner /><span className="ml-2">Loading leads…</span>
            </div>
          )}
          {!loading && error && (
            <div className="p-8">
              <div className="border border-red-200 bg-red-50 rounded-md p-5">
                <p className="text-sm font-semibold text-red-700">Error</p>
                <p className="text-sm text-red-600 mt-1">{error}</p>
              </div>
            </div>
          )}
          {!loading && !error && results.length === 0 && (
            <div className="py-24 text-center text-sm text-zinc-500">No leads match your filters.</div>
          )}
          {!loading && !error && results.length > 0 && (
            <table className="w-full text-sm" style={{ tableLayout: Object.keys(columnWidths).length > 0 ? "fixed" : "auto" }} data-testid="leads-table">
              <thead className="bg-zinc-50 sticky top-0 z-[5]">
                <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
                  {ALL_COLUMNS.filter((c) => visible.includes(c.key)).map((c) => (
                    <th key={c.key} className="relative px-3 py-2.5 font-semibold whitespace-nowrap select-none"
                      style={columnWidths[c.key] ? { width: `${columnWidths[c.key]}px`, minWidth: `${columnWidths[c.key]}px` } : undefined}>
                      {c.label}
                      <span role="separator" aria-orientation="vertical" onMouseDown={(e) => startColumnResize(c.key, e)}
                        onDoubleClick={() => setColumnWidths((prev) => { const next = { ...prev }; delete next[c.key]; persistColumnWidths(next); return next; })}
                        className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize after:absolute after:right-0 after:top-0 after:h-full after:w-px after:bg-zinc-200 after:transition-colors hover:after:bg-zinc-300 active:after:bg-zinc-950" />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {results.map((lead, idx) => {
                  const stRaw = deriveStatus(lead);
                  const stKey = (stRaw || "").toString().toLowerCase().replace(/ /g, "_");
                  const statusMeta = STATUS_LABELS[stKey] || (stRaw ? { label: String(stRaw).toUpperCase(), color: "zinc" } : null);
                  const priceRaw = lead.price ?? lead.list_price;
                  const price = priceRaw ? `$${Number(priceRaw).toLocaleString()}` : null;
                  const amountDue = lead.amount_due ? `$${Number(lead.amount_due).toLocaleString()}` : null;
                  const phone = formatPhone(derivePhone(lead));
                  const email = deriveEmail(lead);
                  const owner = deriveOwner(lead);
                  const date = deriveDate(lead);
                  const renderers = {
                    status: () => statusMeta ? <Badge color={statusMeta.color}>{statusMeta.label}</Badge> : <span className="text-zinc-400">—</span>,
                    date: () => date ? <span className="tabular-nums text-zinc-700">{date}</span> : <span className="text-zinc-400">—</span>,
                    owner: () => owner ? <span className="font-semibold text-zinc-950 max-w-[320px] truncate inline-block align-middle" title={owner}>{owner}</span> : <span className="text-zinc-400">—</span>,
                    address: () => lead.property_address ? <span className="text-zinc-700">{lead.property_address}</span> : <span className="text-zinc-400">—</span>,
                    city: () => lead.property_city ? <span className="text-zinc-700">{lead.property_city}</span> : <span className="text-zinc-400">—</span>,
                    state: () => lead.state ? <span className="text-zinc-700 font-medium">{lead.state}</span> : <span className="text-zinc-400">—</span>,
                    zip: () => lead.property_zip ? <span className="text-zinc-700 tabular-nums">{lead.property_zip}</span> : <span className="text-zinc-400">—</span>,
                    county: () => lead.county_name ? <span className="text-zinc-700">{lead.county_name}</span> : <span className="text-zinc-400">—</span>,
                    property_use: () => lead.property_use ? <span className="text-zinc-700">{lead.property_use}</span> : <span className="text-zinc-400">—</span>,
                    beds: () => lead.bedrooms ? <span className="tabular-nums text-zinc-700">{Number(lead.bedrooms)}</span> : <span className="text-zinc-400">—</span>,
                    baths: () => lead.bathrooms ? <span className="tabular-nums text-zinc-700">{Number(lead.bathrooms)}</span> : <span className="text-zinc-400">—</span>,
                    sqft: () => lead.sqft ? <span className="tabular-nums text-zinc-700">{Number(lead.sqft).toLocaleString()}</span> : <span className="text-zinc-400">—</span>,
                    year_built: () => { const y = deriveYearBuilt(lead); return y ? <span className="tabular-nums text-zinc-700">{y}</span> : <span className="text-zinc-400">—</span>; },
                    price: () => price ? <span className="font-semibold text-zinc-950 tabular-nums">{price}</span> : <span className="text-zinc-400">—</span>,
                    amount_due: () => amountDue ? <span className="font-semibold text-amber-700 tabular-nums">{amountDue}</span> : <span className="text-zinc-400">—</span>,
                    institution: () => lead.institution_name ? <span className="text-zinc-700 truncate max-w-[180px] inline-block align-middle" title={lead.institution_name}>{lead.institution_name}</span> : <span className="text-zinc-400">—</span>,
                    phone: () => phone ? <span className="inline-flex items-center gap-1.5 font-mono text-xs text-zinc-950"><Phone className="h-3 w-3 text-zinc-400" /> {phone}</span> : <span className="text-zinc-400">—</span>,
                    email: () => email ? <a href={`mailto:${email}`} className="text-zinc-950 hover:underline truncate max-w-[200px] inline-block align-middle" title={email}>{email}</a> : <span className="text-zinc-400">—</span>,
                  };
                  return (
                    <tr key={lead.id || idx} className="border-b border-zinc-100 odd:bg-white even:bg-zinc-50/40 hover:bg-zinc-100/60 transition-colors" data-testid={`lead-row-${idx}`}>
                      {ALL_COLUMNS.filter((c) => visible.includes(c.key)).map((c) => (
                        <td key={c.key} className="px-3 py-2 whitespace-nowrap overflow-hidden text-ellipsis cursor-pointer"
                          style={columnWidths[c.key] ? { width: `${columnWidths[c.key]}px`, minWidth: `${columnWidths[c.key]}px`, maxWidth: `${columnWidths[c.key]}px` } : undefined}
                          onClick={() => openLeadDetails(lead, idx)}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openLeadDetails(lead, idx); } }}
                          tabIndex={0} role="button">
                          {renderers[c.key]?.()}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-6 py-3 border-t border-zinc-200 bg-white flex-shrink-0">
          <span className="text-sm text-zinc-600">Page {page.toLocaleString()} of {totalPages.toLocaleString()} — {total.toLocaleString()} results</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" disabled={page <= 1} onClick={() => handlePageChange(page - 1)}>
              <ChevronLeft className="h-4 w-4 mr-1" /> Prev
            </Button>
            <Button variant="outline" disabled={page >= totalPages} onClick={() => handlePageChange(page + 1)}>
              Next <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
